import { useQuery } from "@tanstack/react-query";
import React, { type ReactNode, createContext, useContext, useEffect, useRef } from "react";

import { ASYNC_TASK_NAMES } from "@performyard/models";
import { toast } from "@performyard/py-components";
import { GetReviewCycleDetailsResponseFrontend } from "@performyard/service-contracts/src/review-cycle/response";
import { ReviewDetailsFrontend } from "@performyard/service-contracts/src/reviews/review-details";

import { useSessionStore } from "../../../../containers/SessionManager/sessionStore";

import { useReviewCycleDetails, useReviewCycleReviewsDetailsStream } from "../../../../api/hooks/defs/review-cycle";
import { useAsyncTaskStream } from "../../../../hooks/useAsyncTaskStream";

import { getActiveCompanyTasksByName } from "../../../../api/services/defs/internal-api/async-tasks";
import { useReviewCycleStatus } from "../../../../api/websocket/review-cycle-status";

import ReviewCycleLoader from "../LoadingStates/ReviewCycleLoader";

type ReviewCycleBuilderPageContextType = {
	renderPage: ReactNode | null;
	fullRefresh: () => void;
	reviewCycleDetails?: GetReviewCycleDetailsResponseFrontend;
	reviewCycleReviewsDetails?: ReviewDetailsFrontend[];
};

const ReviewCycleBuilderPageContext = createContext<ReviewCycleBuilderPageContextType | undefined>(undefined);

type ReviewCycleBuilderPageProviderProps = {
	children: ReactNode;
	reviewCycleId: string;
};

export const ReviewCycleBuilderPageProvider = ({ children, reviewCycleId }: ReviewCycleBuilderPageProviderProps) => {
	const {
		data: activeCreateTasks,
		isPending: isActiveCreateTasksPending,
		refetch: refetchActiveCreateTasks,
	} = useQuery({
		queryKey: ["active-create-review-cycle-tasks", reviewCycleId],
		queryFn: () => getActiveCompanyTasksByName(ASYNC_TASK_NAMES.CREATE_REVIEW_CYCLE),
		meta: { disableDefaultErrorHandling: true },
	});
	const activeCreateTask = activeCreateTasks?.find((t) => {
		const metadata = t.metadata as { cycleId?: string } | undefined;
		return metadata?.cycleId === reviewCycleId;
	});
	const buildTaskId = activeCreateTask?._id ?? null;
	const { task: streamedBuildTask, error: buildTaskError } = useAsyncTaskStream(buildTaskId);
	const buildTaskStatus = streamedBuildTask?.status ?? activeCreateTask?.status;
	const isBuildingFromTask = !!buildTaskId && buildTaskStatus !== "completed" && buildTaskStatus !== "failed";

	// The v2 Add Subjects flow enqueues an async task (not a legacy async_job), so
	// the jobs-status WebSocket below never reports it. Mirror the build-task
	// tracking so the same "Adding Subjects..." loader shows from either source.
	const {
		data: activeAddSubjectsTasks,
		isPending: isActiveAddSubjectsTasksPending,
		refetch: refetchActiveAddSubjectsTasks,
	} = useQuery({
		queryKey: ["active-add-subjects-tasks", reviewCycleId],
		queryFn: () => getActiveCompanyTasksByName(ASYNC_TASK_NAMES.ADD_SUBJECTS_TO_REVIEW_CYCLE),
		meta: { disableDefaultErrorHandling: true },
	});
	const activeAddSubjectsTask = activeAddSubjectsTasks?.find((t) => {
		const metadata = t.metadata as { cycleId?: string } | undefined;
		return metadata?.cycleId === reviewCycleId;
	});
	const addSubjectsTaskId = activeAddSubjectsTask?._id ?? null;
	const { task: streamedAddSubjectsTask } = useAsyncTaskStream(addSubjectsTaskId);
	const addSubjectsTaskStatus = streamedAddSubjectsTask?.status ?? activeAddSubjectsTask?.status;
	const isAddingSubjectsFromTask =
		!!addSubjectsTaskId && addSubjectsTaskStatus !== "completed" && addSubjectsTaskStatus !== "failed";

	const cycleQueriesEnabled =
		!isBuildingFromTask &&
		!isActiveCreateTasksPending &&
		!isAddingSubjectsFromTask &&
		!isActiveAddSubjectsTasksPending;

	// Use the new unified review cycle status WebSocket
	const {
		isConnected,
		isInitialized: statusInitialized,
		isConnecting,
		isBuilding,
		isAddingSubjects,
		isApprovingEasyAdds,
		isAnyJobRunning,
		isUpdating,
		resetConnection,
		connect,
		disconnect,
	} = useReviewCycleStatus(reviewCycleId, false);

	// Subscribed so the Scenario-2 effect re-runs when the session is restored
	// after expiration — otherwise its deps don't change and the WS never reconnects.
	const isSessionExpired = useSessionStore((s) => s.isExpired);

	// Track when queries are enabled/disabled
	// Use either WebSocket status or fallback after timeout
	const webSocketReady = statusInitialized;

	const {
		data: reviewCycleDetails,
		isLoading: isReviewCycleDetailsLoading,
		refetch: refetchReviewCycleDetails,
	} = useReviewCycleDetails(reviewCycleId, {
		enabled: cycleQueriesEnabled,
		retry: (attemptIndex) => {
			// If we detect building via websockets, keep retrying quietly
			if (isAnyJobRunning) {
				return attemptIndex < 10; // Retry more when building is detected
			}
			// Normal retry logic - be more lenient initially in case of race conditions
			return attemptIndex < 5;
		},
		retryDelay: (attemptIndex) => {
			// Faster retries initially to handle race conditions with quick jobs
			if (attemptIndex <= 3) {
				return Math.min(100 * 2 ** attemptIndex, 500); // 100ms, 200ms, 400ms, 500ms
			}
			return Math.min(1000 * 2 ** (attemptIndex - 3), 5000); // Then longer delays
		},
		meta: {
			disableDefaultErrorHandling: true, // Always suppress errors - we handle them with our own loading states
		},
	});
	const {
		data: reviewCycleReviewsDetails,
		refetch: refetchReviewCycleReviewsDetails,
		resetQuery: resetReviewCycleReviewsDetailsQuery,
	} = useReviewCycleReviewsDetailsStream(reviewCycleId, undefined, {
		enabled: cycleQueriesEnabled,
		retry: (attemptIndex) => {
			// If we detect building via websockets, keep retrying quietly
			if (isAnyJobRunning) {
				return attemptIndex < 10; // Retry more when building is detected
			}
			// Normal retry logic - be more lenient initially in case of race conditions
			return attemptIndex < 5;
		},
		retryDelay: (attemptIndex) => {
			// Faster retries initially to handle race conditions with quick jobs
			if (attemptIndex <= 3) {
				return Math.min(100 * 2 ** attemptIndex, 500); // 100ms, 200ms, 400ms, 500ms
			}
			return Math.min(1000 * 2 ** (attemptIndex - 3), 5000); // Then longer delays
		},
		meta: {
			disableDefaultErrorHandling: true, // Always suppress errors - we handle them with our own loading states
		},
		staleTime: 2000, // Override the infinite stale time to allow refetching when needed. 2 seconds to allow reusing initial loader request
		refetchOnMount: true, // Always refetch when component mounts
	});

	const fullRefresh = (): void => {
		// Reset WebSocket connection to get fresh status
		resetConnection();
		// Reset queries
		resetReviewCycleReviewsDetailsQuery();
		refetchReviewCycleDetails();
		refetchReviewCycleReviewsDetails();
	};

	// Refetch queries whenever job running state changes, but only after WS is connected/initialized
	const prevIsAnyJobRunningRef = useRef<boolean | null>(null);
	useEffect(() => {
		if (!statusInitialized || !isConnected) {
			return;
		}

		if (prevIsAnyJobRunningRef.current === null) {
			prevIsAnyJobRunningRef.current = isAnyJobRunning;
			return;
		}

		if (prevIsAnyJobRunningRef.current !== isAnyJobRunning) {
			// Job state toggled; refresh queries and let UI show loader when jobs run
			resetReviewCycleReviewsDetailsQuery();
			refetchReviewCycleDetails();
			refetchReviewCycleReviewsDetails();
			prevIsAnyJobRunningRef.current = isAnyJobRunning;
		}
	}, [
		isAnyJobRunning,
		statusInitialized,
		isConnected,
		resetReviewCycleReviewsDetailsQuery,
		refetchReviewCycleDetails,
		refetchReviewCycleReviewsDetails,
	]);

	useEffect(() => {
		if (streamedBuildTask?.status === "completed") {
			refetchReviewCycleDetails();
			refetchReviewCycleReviewsDetails();
			refetchActiveCreateTasks();
		}
	}, [
		streamedBuildTask?.status,
		refetchReviewCycleDetails,
		refetchReviewCycleReviewsDetails,
		refetchActiveCreateTasks,
	]);

	// Once the add-subjects task finishes, pull the cycle + reviews again so the
	// newly added subjects render, refresh the active-task query so the loader
	// clears, and toast the outcome. The toast fires here (not on submit) because
	// only now do we know the subjects were actually added. A ref keyed by task id
	// guards against re-firing across re-renders and against toasting for a task
	// that was already finished when we first landed on the page.
	const toastedAddSubjectsTaskRef = useRef<string | null>(null);
	useEffect(() => {
		const status = streamedAddSubjectsTask?.status;
		if (status !== "completed" && status !== "failed") {
			return;
		}
		if (!addSubjectsTaskId || toastedAddSubjectsTaskRef.current === addSubjectsTaskId) {
			return;
		}
		toastedAddSubjectsTaskRef.current = addSubjectsTaskId;

		if (status === "completed") {
			refetchReviewCycleDetails();
			refetchReviewCycleReviewsDetails();
			refetchActiveAddSubjectsTasks();
			toast.success("Subjects were added to the cycle.");
		} else {
			toast.error("There was an error adding subjects to the cycle.");
		}
	}, [
		addSubjectsTaskId,
		streamedAddSubjectsTask?.status,
		refetchReviewCycleDetails,
		refetchReviewCycleReviewsDetails,
		refetchActiveAddSubjectsTasks,
	]);

	const jobStatus = reviewCycleDetails?.jobStatus || "idle";

	// Scenario 1: If initial load shows preloader AND server reports a non-idle job,
	// initiate websocket connection and wait for handshake (statusInitialized) before proceeding
	useEffect(() => {
		if (!reviewCycleId || isSessionExpired) {
			return;
		}
		if (jobStatus !== "idle" && !statusInitialized && !isConnected && !isConnecting) {
			connect(reviewCycleId);
		}
	}, [reviewCycleId, jobStatus, statusInitialized, isConnected, isConnecting, isSessionExpired, connect]);

	// Scenario 2: Once content is visible, ensure websocket is connected non-blocking.
	// isSessionExpired is in the dep list so that when the session is restored after
	// expiration (falling edge), this effect re-runs and reconnects.
	useEffect(() => {
		if (!reviewCycleId || isSessionExpired) {
			return;
		}
		const contentVisible =
			!isReviewCycleDetailsLoading && (statusInitialized || jobStatus === "idle") && !isAnyJobRunning;
		if (contentVisible && !isConnected && !isConnecting) {
			connect(reviewCycleId);
		}
	}, [
		reviewCycleId,
		isReviewCycleDetailsLoading,
		statusInitialized,
		jobStatus,
		isAnyJobRunning,
		isConnected,
		isConnecting,
		isSessionExpired,
		connect,
	]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (isConnected) {
				disconnect();
			}
		};
	}, [isConnected, disconnect]);

	// Render page logic based on job statuses
	let renderPage: ReactNode | null = null;
	if (buildTaskStatus === "failed" || buildTaskError) {
		renderPage = <ReviewCycleLoader text={streamedBuildTask?.error || "Failed to create review cycle"} />;
	} else if (
		isBuildingFromTask ||
		isAddingSubjectsFromTask ||
		isReviewCycleDetailsLoading ||
		(!webSocketReady && jobStatus !== "idle") ||
		isAnyJobRunning
	) {
		let text: string = "";
		if (isBuildingFromTask || isBuilding || reviewCycleDetails?.jobStatus === "building") {
			text = "Your Review Cycle is Still Building...";
		} else if (isAddingSubjectsFromTask || isAddingSubjects) {
			text = "Adding Subjects to the Cycle...";
		} else if (isApprovingEasyAdds) {
			text = "Approving 360 forms...";
		} else if (isUpdating || reviewCycleDetails?.jobStatus === "updating") {
			text = "Updating the Cycle...";
		} else {
			text = "Loading Review Cycle...";
		}
		renderPage = <ReviewCycleLoader text={text} />;
	} else {
		renderPage = null;
	}

	const value: ReviewCycleBuilderPageContextType = {
		renderPage,
		fullRefresh,
		reviewCycleDetails,
		reviewCycleReviewsDetails,
	};

	return <ReviewCycleBuilderPageContext.Provider value={value}>{children}</ReviewCycleBuilderPageContext.Provider>;
};

export const useReviewCycleBuilderPageContext = (): ReviewCycleBuilderPageContextType => {
	const context = useContext(ReviewCycleBuilderPageContext);
	if (context === undefined) {
		throw new Error("useReviewCycleBuilderPageContext must be used within a ReviewCycleBuilderPageProvider");
	}
	return context;
};
